import {
  BackSide,
  BoxGeometry,
  BufferGeometry,
  Color,
  ConeGeometry,
  DirectionalLight,
  Float32BufferAttribute,
  Fog,
  Group,
  HemisphereLight,
  InstancedMesh,
  Line,
  LineDashedMaterial,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  SphereGeometry,
  type Material,
  type Scene,
} from 'three';

/**
 * Reusable world theme system (GDD §14.1, §14.2, issue #68). A theme is pure data (palette, fog, lights
 * and parallax layers); `applyTheme` turns it into scene objects. The world palette is game art, never
 * design system tokens (GDD §14.1, §17-3).
 *
 * Layers use real depth (GDD §14.1): the camera travels at z = +19 and looks along -z, so a layer farther
 * back (z = -60) sweeps the screen more slowly than one closer to the play plane (z = -15) for the same
 * world displacement. Every layer scrolls at the level speed, so the perspective alone makes parallax.
 */

export type ThemeLayerName = 'layer.foreground' | 'layer.mid' | 'layer.far';

/** The z of each parallax layer (GDD §14.1): foreground +3, mid -15, far -60. */
export const LAYER_Z: Readonly<Record<ThemeLayerName, number>> = { 'layer.foreground': 3, 'layer.mid': -15, 'layer.far': -60 };

/** Depth of the neon signs of a night theme (#102): between the mid and the far layers, never in front of the mid. */
export const NEON_Z = -30;

/** Depth of the bridge of the New York sky (#112): between the mid and the far layers. */
export const BRIDGE_Z = -40;

/** Depth of the road signs of the El Paso sky (#123): between the mid layer and the bridge depth. */
export const SIGNS_Z = -25;

export interface ThemeLight {
  readonly color: number;
  readonly intensity: number;
}

export interface ThemeLayer {
  readonly name: ThemeLayerName;
  /** Palette of the layer, in order of use. */
  readonly colors: readonly number[];
  /** Horizontal period of the repeated scenery, in u. */
  readonly moduleWidth: number;
  /** Nominal height of the modules, in u. */
  readonly height: number;
  /** Base altitude of the modules, in u. */
  readonly baseY: number;
  readonly motif: 'hills' | 'buildings' | 'foreground';
  /** A layer pinned to the camera (a band below the ground) covers the bottom everywhere; the rest are world-static. */
  readonly followCamera?: boolean;
}

export interface WorldTheme {
  readonly id: string;
  readonly variant: string;
  readonly sky: number;
  readonly fog: { readonly color: number; readonly near: number; readonly far: number };
  readonly ground: number;
  readonly ceiling: number;
  /** Tint of the greybox collidable geometry (columns and pickups): more saturated than the decoration (GDD §14.1). */
  readonly collidable: { readonly column: number; readonly pickup: number };
  readonly lights: {
    readonly hemisphere: { readonly sky: number; readonly ground: number; readonly intensity: number };
    readonly sun: ThemeLight & { readonly position: readonly [number, number, number] };
  };
  readonly layers: readonly ThemeLayer[];
  /**
   * Neon accents of a night city (issue #102): colours of emissive signs that hang far behind the play plane
   * (z = -30), dimmer than anything that collides. Absent in the themes without neon.
   */
  readonly neon?: readonly number[];
  /**
   * A suspension bridge in the distance (the New York sky, issue #112): colours of the deck and of the towers. It
   * is decoration at z = -40, between the mid and the far layers, and never imitates an obstacle.
   */
  readonly bridge?: { readonly deck: number; readonly tower: number };
  /**
   * Road signs of the highway in the distance (the El Paso sky, issue #123): colours of the posts and of the blank
   * boards (no text, no brand). Decoration at z = -25, well behind the play plane, never an obstacle.
   */
  readonly signs?: { readonly post: number; readonly board: number };
}

/** HSL saturation (0 to 1) of a colour, the legibility metric of GDD §14.1 (issue #68). */
export function hslSaturation(color: number): number {
  const hsl = { h: 0, s: 0, l: 0 };
  new Color(color).getHSL(hsl);
  return hsl.s;
}

/** Relative luminance of a colour (WCAG 2.x), for the contrast threshold of GDD §14.1 (#114). */
export function relativeLuminance(color: number): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const r = channel((color >> 16) & 0xff);
  const g = channel((color >> 8) & 0xff);
  const b = channel(color & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio between two colours (1 to 21), the WCAG metric used for the legibility threshold. */
export function contrastRatio(a: number, b: number): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

const isHex = (value: number): boolean => Number.isInteger(value) && value >= 0 && value <= 0xffffff;

/** Rejects a malformed theme before it touches the scene. */
export function assertValidTheme(theme: WorldTheme): void {
  if (!theme.id.trim()) throw new Error('theme id is empty');
  if (!theme.variant.trim()) throw new Error(`theme "${theme.id}" has no variant`);
  for (const [what, color] of [
    ['sky', theme.sky],
    ['fog', theme.fog.color],
    ['ground', theme.ground],
    ['ceiling', theme.ceiling],
    ['column tint', theme.collidable.column],
    ['pickup tint', theme.collidable.pickup],
  ] as const) {
    if (!isHex(color)) throw new Error(`theme "${theme.id}" has an invalid ${what} colour`);
  }
  const names = new Set<ThemeLayerName>();
  for (const layer of theme.layers) {
    if (LAYER_Z[layer.name] === undefined) throw new Error(`theme "${theme.id}" has an unknown layer "${layer.name}"`);
    names.add(layer.name);
    if (layer.colors.length === 0 || layer.colors.some((color) => !isHex(color))) throw new Error(`theme "${theme.id}" has an invalid palette in "${layer.name}"`);
    if (layer.moduleWidth <= 0 || layer.height <= 0) throw new Error(`theme "${theme.id}" has invalid geometry in "${layer.name}"`);
  }
  if (theme.neon?.some((color) => !isHex(color))) throw new Error(`theme "${theme.id}" has an invalid neon colour`);
  if (theme.bridge && (!isHex(theme.bridge.deck) || !isHex(theme.bridge.tower))) throw new Error(`theme "${theme.id}" has an invalid bridge colour`);
  if (theme.signs && (!isHex(theme.signs.post) || !isHex(theme.signs.board))) throw new Error(`theme "${theme.id}" has an invalid sign colour`);
  if (names.size !== 3) throw new Error(`theme "${theme.id}" must define the three parallax layers`);
}

/**
 * Half of the visible width at depth z, from the pinhole camera of GDD §14.1: a camera at distance D
 * sees `viewWidth` at z = 0, so at depth z it sees `viewWidth × (D − z) / D` (a negative z is farther).
 */
export function visibleHalfWidth(z: number, cameraDistance: number, viewWidth: number): number {
  return (viewWidth * (cameraDistance - z)) / (2 * cameraDistance);
}

export interface AppliedTheme {
  readonly group: Group;
  info(): { id: string; variant: string; sky: number; background: number; fog: number; decorationCounts: Record<string, number> };
  /**
   * Pins the ground, the ceiling line, the sky and the camera-pinned band to the camera. The parallax
   * layers stay fixed in world space; the camera travels with the plane, so for the same world scroll the
   * far layer (z = -60) sweeps the screen less than the mid one (z = -15): real-depth parallax (GDD §14.1).
   */
  follow(cameraX: number, cameraY: number, motionScale?: number): void;
  /**
   * Where a world layer is on the screen, in view units at the play plane, as the horizontal gap between the layer
   * and the camera seen through the perspective (issue #139). It changes by `motionScale` times the camera travel.
   */
  layerScreenX(name: ThemeLayerName | 'layer.neon' | 'layer.bridge' | 'layer.signs', cameraX: number): number;
  /** Fades the whole theme (sky, ground, decoration) by `value` in [0, 1], for a crossfade transition (#97). */
  setOpacity(value: number): void;
  dispose(): void;
}

export interface ApplyThemeOptions {
  readonly groundWidth: number;
  readonly groundY: number;
  readonly worldTop: number;
  readonly cameraDistance: number;
  readonly viewWidth: number;
  /** Total length of the level, to span the parallax layers over it. */
  readonly levelLength: number;
}

const motifGeometry = (layer: ThemeLayer): BufferGeometry => {
  if (layer.motif === 'hills') return new ConeGeometry(layer.moduleWidth * 0.7, layer.height, 5);
  if (layer.motif === 'buildings') return new BoxGeometry(layer.moduleWidth * 0.5, layer.height, layer.moduleWidth * 0.4);
  return new BoxGeometry(layer.moduleWidth * 0.85, layer.height, 1);
};

/**
 * Builds the sky, the lights, the ground, the ceiling line and the three parallax layers of a theme and
 * adds them to the scene. The caller owns the returned handle: `scroll`/`follow` each frame and `dispose`
 * when the level (or the theme) changes, so nothing leaks (issue #68 memory scenario).
 */
export function applyTheme(scene: Scene, theme: WorldTheme, options: ApplyThemeOptions): AppliedTheme {
  assertValidTheme(theme);
  const background = new Color(theme.sky);
  scene.background = background;
  scene.fog = new Fog(theme.fog.color, theme.fog.near, theme.fog.far);

  const hemi = new HemisphereLight(theme.lights.hemisphere.sky, theme.lights.hemisphere.ground, theme.lights.hemisphere.intensity);
  const sun = new DirectionalLight(theme.lights.sun.color, theme.lights.sun.intensity);
  sun.position.set(...theme.lights.sun.position);
  scene.add(hemi, sun, sun.target);

  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  // An emissive material: it keeps its colour in the dark, as a neon sign does.
  const glow = (color: number) => {
    const material = new MeshLambertMaterial({ color, emissive: color, emissiveIntensity: 1, flatShading: true });
    materials.push(material);
    return material;
  };

  const group = new Group();
  group.name = `theme:${theme.id}:${theme.variant}`;
  scene.add(group);

  // Sky: a sphere around the camera, behind everything (GDD §14.1). Unlit and never fogged, so the
  // gradient reads as the sky and not as a lit surface.
  const skyMaterial = new MeshBasicMaterial({ color: theme.sky, side: BackSide, fog: false });
  materials.push(skyMaterial);
  const sky = new Mesh(own(new SphereGeometry(200, 16, 12)), skyMaterial);
  sky.name = 'layer.sky';
  group.add(sky);

  // Ground: its top face is the contact line (GDD §5.2). Pinned to the camera in `follow`.
  const ground = new Mesh(own(new BoxGeometry(options.groundWidth, options.groundY, 4)), paint(theme.ground));
  ground.name = 'world.ground';
  ground.position.y = options.groundY / 2;
  group.add(ground);

  // Ceiling: the dashed line at the top of the band (GDD §5.2). Pinned to the camera in `follow`.
  const ceilingGeometry = own(new BufferGeometry());
  ceilingGeometry.setAttribute('position', new Float32BufferAttribute([-options.groundWidth / 2, 0, 0, options.groundWidth / 2, 0, 0], 3));
  const ceilingMaterial = new LineDashedMaterial({ color: theme.ceiling, dashSize: 0.6, gapSize: 0.4 });
  materials.push(ceilingMaterial);
  const ceiling = new Line(ceilingGeometry, ceilingMaterial);
  ceiling.computeLineDistances();
  ceiling.name = 'world.ceiling-line';
  ceiling.position.y = options.worldTop;
  group.add(ceiling);

  // One batch per decoration type; its palette travels in instanceColor (#70).
  const pinned: Group[] = [];
  // The world-static decoration, which the reduced motion setting follows the camera with (issue #139).
  const parallax: Group[] = [];
  const decorationCounts: Record<string, number> = {};
  for (const layer of theme.layers) {
    const layerGroup = new Group();
    layerGroup.name = layer.name;
    // World-static layers start at the level origin; the camera-pinned band is re-centred every frame.
    layerGroup.position.set(layer.followCamera ? 0 : 0, layer.baseY, LAYER_Z[layer.name]);
    const geometry = own(motifGeometry(layer));
    const spacing = layer.moduleWidth;
    // World layers span the whole level plus a view width on each side; a camera-pinned band just covers the view.
    const count = layer.followCamera
      ? Math.max(2, Math.ceil(options.viewWidth / spacing) + 4)
      : Math.max(2, Math.ceil((options.levelLength + options.viewWidth * 2) / spacing));
    const start = layer.followCamera ? -count * spacing * 0.5 : -options.viewWidth;
    const matrix = new Matrix4();
    const mesh = new InstancedMesh(geometry, paint(0xffffff), count);
    mesh.name = `${layer.name}.${layer.motif === 'buildings' ? 'houses' : layer.motif}.instanced`;
    decorationCounts[mesh.name] = count;
    mesh.userData.instanceIds = Array.from({ length: count }, (_, index) => `${layer.name}:${index}`);
    const instanceColor = new Color();
    for (let i = 0; i < count; i++) {
      const x = start + i * spacing + spacing / 2;
      // A deterministic two-step height, so the skyline reads as scenery and never as an obstacle.
      const scale = 0.7 + 0.3 * (((i * 7) % 3) / 2);
      matrix.makeScale(1, scale, 1);
      matrix.setPosition(x, 0, 0);
      mesh.setMatrixAt(i, matrix);
      mesh.setColorAt(i, instanceColor.setHex(layer.colors[i % layer.colors.length]!));
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.instanceColor!.needsUpdate = true;
    layerGroup.add(mesh);
    group.add(layerGroup);
    if (layer.followCamera) pinned.push(layerGroup);
    else parallax.push(layerGroup);
  }

  // Neon signs of a night city: low horizontal bars, one InstancedMesh per colour, far behind the mid layer.
  if (theme.neon && theme.neon.length > 0) {
    const neonGroup = new Group();
    neonGroup.name = 'layer.neon';
    neonGroup.position.set(0, 4, NEON_Z);
    const bar = own(new BoxGeometry(5, 0.35, 0.3));
    const spacing = 11;
    const count = Math.max(2, Math.ceil((options.levelLength + options.viewWidth * 2) / spacing));
    const buckets = theme.neon.map((color) => ({ color, matrices: [] as Matrix4[] }));
    const matrix = new Matrix4();
    for (let i = 0; i < count; i++) {
      // Deterministic heights, so the signs scatter over the skyline.
      matrix.makeScale(1, 1, 1);
      matrix.setPosition(-options.viewWidth + i * spacing + spacing / 2, ((i * 5) % 4) * 1.1, 0);
      buckets[i % buckets.length]!.matrices.push(matrix.clone());
    }
    buckets.forEach((bucket, index) => {
      if (bucket.matrices.length === 0) return;
      const mesh = new InstancedMesh(bar, glow(bucket.color), bucket.matrices.length);
      mesh.name = `layer.neon:${index}`;
      bucket.matrices.forEach((value, at) => mesh.setMatrixAt(at, value));
      mesh.instanceMatrix.needsUpdate = true;
      neonGroup.add(mesh);
    });
    group.add(neonGroup);
    parallax.push(neonGroup);
  }

  // A suspension bridge every 80 u: a long deck and two towers, as instances (one draw call each).
  if (theme.bridge) {
    const bridgeGroup = new Group();
    bridgeGroup.name = 'layer.bridge';
    bridgeGroup.position.set(0, 0, BRIDGE_Z);
    const spacing = 80;
    const count = Math.max(2, Math.ceil((options.levelLength + options.viewWidth * 2) / spacing));
    const deckMatrices: Matrix4[] = [];
    const towerMatrices: Matrix4[] = [];
    const matrix = new Matrix4();
    for (let i = 0; i < count; i++) {
      const centre = -options.viewWidth + i * spacing + spacing / 2;
      matrix.makeScale(1, 1, 1);
      matrix.setPosition(centre, 6, 0);
      deckMatrices.push(matrix.clone());
      for (const side of [-1, 1]) {
        matrix.setPosition(centre + side * 26, 9, 0);
        towerMatrices.push(matrix.clone());
      }
    }
    for (const [name, geometry, color, matrices] of [
      ['deck', own(new BoxGeometry(70, 0.8, 1.6)), theme.bridge.deck, deckMatrices],
      ['tower', own(new BoxGeometry(1.4, 16, 1.4)), theme.bridge.tower, towerMatrices],
    ] as const) {
      const mesh = new InstancedMesh(geometry, paint(color), matrices.length);
      mesh.name = `layer.bridge:${name}`;
      matrices.forEach((value, at) => mesh.setMatrixAt(at, value));
      mesh.instanceMatrix.needsUpdate = true;
      bridgeGroup.add(mesh);
    }
    group.add(bridgeGroup);
    parallax.push(bridgeGroup);
  }

  // Road signs every 36 u: a thin post with a wide blank board on top, as instances (one draw call each).
  if (theme.signs) {
    const signGroup = new Group();
    signGroup.name = 'layer.signs';
    signGroup.position.set(0, 0, SIGNS_Z);
    const spacing = 36;
    const count = Math.max(2, Math.ceil((options.levelLength + options.viewWidth * 2) / spacing));
    const postMatrices: Matrix4[] = [];
    const boardMatrices: Matrix4[] = [];
    const matrix = new Matrix4();
    for (let i = 0; i < count; i++) {
      const x = -options.viewWidth + i * spacing + spacing / 2;
      matrix.makeScale(1, 1, 1);
      matrix.setPosition(x, 2.25, 0);
      postMatrices.push(matrix.clone());
      matrix.setPosition(x, 4.9, 0);
      boardMatrices.push(matrix.clone());
    }
    for (const [name, geometry, color, matrices] of [
      ['post', own(new BoxGeometry(0.3, 4.5, 0.3)), theme.signs.post, postMatrices],
      ['board', own(new BoxGeometry(3.2, 1.5, 0.2)), theme.signs.board, boardMatrices],
    ] as const) {
      const mesh = new InstancedMesh(geometry, paint(color), matrices.length);
      mesh.name = `layer.signs:${name}`;
      matrices.forEach((value, at) => mesh.setMatrixAt(at, value));
      mesh.instanceMatrix.needsUpdate = true;
      signGroup.add(mesh);
    }
    group.add(signGroup);
    parallax.push(signGroup);
  }

  return {
    group,
    info: () => ({ id: theme.id, variant: theme.variant, sky: theme.sky, background: background.getHex(), fog: theme.fog.color, decorationCounts: { ...decorationCounts } }),
    follow(cameraX, cameraY, motionScale = 1) {
      // The layers stay in world space and the camera travels with the plane, so the perspective alone makes the
      // parallax. Reduced motion (motionScale 0.5) carries each layer along with the camera by the rest of the
      // travel, so it sweeps the screen at that fraction of its speed; the play plane is never touched.
      for (const layer of parallax) layer.position.x = (1 - motionScale) * cameraX;
      ground.position.x = cameraX;
      ceiling.position.x = cameraX;
      sky.position.set(cameraX, cameraY, 0);
      for (const layer of pinned) layer.position.x = cameraX;
    },
    layerScreenX(name, cameraX) {
      const layer = group.getObjectByName(name);
      if (!layer) throw new Error(`theme "${theme.id}" has no layer "${name}"`);
      return ((layer.position.x - cameraX) * options.cameraDistance) / (options.cameraDistance - layer.position.z);
    },
    setOpacity(value) {
      const clamped = Math.max(0, Math.min(1, value));
      for (const material of materials) {
        material.transparent = clamped < 1;
        material.opacity = clamped;
        material.depthWrite = clamped >= 1;
        material.needsUpdate = true;
      }
    },
    dispose() {
      scene.remove(hemi, sun, sun.target, group);
      group.traverse((object) => { if (object instanceof InstancedMesh) object.dispose(); });
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
